import {isAsyncThunkAction} from '@reduxjs/toolkit'

import {registerOrderV1, registerOrderV2} from '@web/actions/orderSheetInfo'
import {fetchWaitingState} from '@web/actions/waiting'
import {
    ErrorMessage,
    ErrorType,
    OrderFailureType,
    ReasonTypeType,
    ReloadNotAllowedReason,
    UserAuthStatus,
    ValidationFailureCodes,
} from '@web/core/constants'
import {PAY_M, PC_PAY} from '@web/core/urls'
import {GwError} from '@web/helpers/api'
import {getQuery} from '@web/helpers/path'
import {TranslationSetType} from '@web/i18n'
import authCreator, {AuthCreatorProps} from '@web/middlewares/auth'
import {orderInterrupted, OrderInterruptType} from '@web/middlewares/orderInterrupted'

import {initOrderSheetByAPI} from '../../actions'
import {getOrderResultUrl} from '../../utils'

import type {RootState} from '../../interfaces'

const translation: TranslationSetType = {
    ko: {
        '주문이 이미 완료되었습니다.': '이미 결제가 완료된 주문입니다. 주문완료 화면으로 이동합니다',
        '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.':
            '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.',
    },
    en: {
        '주문이 이미 완료되었습니다.': 'Your order has already been completed.',
        '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.':
            'Data retrieval failed due to a temporary issue. Please try again later.',
    },
    ja: {
        '주문이 이미 완료되었습니다.': '既に注文が完了しています。',
        '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.':
            '一時的にエラーが発生し、データ照会に失敗しました。しばらくしてからもう一度お試しください。',
    },
    zh: {
        '주문이 이미 완료되었습니다.': '下单成功。',
        '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.': '由于暂时性问题，数据查询失败，请稍后再试。',
    },
}

/** 주문서 로딩되기 전 액션들 모음 */
const isInitActions = isAsyncThunkAction(initOrderSheetByAPI, fetchWaitingState, registerOrderV1, registerOrderV2)

/**
 * order App 이용정지 case Handler
 */
const transactionStopHandler: AuthCreatorProps<RootState>['transactionStopHandler'] = ({
    next,
    action,
    isMobileDisplay,
}) => {
    const error = JSON.parse(action.error.message) as GwError
    const {
        data: {message: reason},
    } = error

    if (isInitActions(action)) {
        const url = isMobileDisplay ? `${PAY_M}/mobile/memberStop` : `${PAY_M}/memberStop`
        return next(
            orderInterrupted([
                {
                    type: 'REDIRECT',
                    url,
                    reason,
                },
            ]),
        )
    } else {
        if (isMobileDisplay) {
            return next(
                orderInterrupted([
                    {
                        type: 'ALERT',
                        message:
                            '회원님은 네이버페이 서비스 이용이 중지되었습니다. 관련 문의 사항은 네이버페이 고객센터로 문의하세요.',
                        reason,
                    },
                ]),
            )
        } else {
            return next(
                orderInterrupted([
                    {
                        type: 'POPUP',
                        props: [`${PC_PAY}/memberStopPopup`, 'stopPopup', 'width=520,height=600,alwaysRaised=yes'],
                        reason,
                    },
                ]),
            )
        }
    }
}

/**
 * order App VALIDATION_FAILED case Handler
 */
const validationFailureHandler: AuthCreatorProps<RootState>['validationFailureHandler'] = ({next, action, state}) => {
    const code = action.payload?.code ?? ''
    const backUrl = state.common.backUrl || getQuery<{backUrl: string}>()?.backUrl
    const isAnonymousUser = action?.payload?.userAuthStatus === UserAuthStatus.ANONYMOUS
    const language = isAnonymousUser ? 'en' : 'ko'

    if (code === ErrorType.ETIMEDOUT_FAILED) {
        const message = isAnonymousUser
            ? translation[language]['일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.']
            : action?.payload?.fallbackMessage
        return next(
            orderInterrupted([
                {
                    type: 'ALERT',
                    message: message ?? '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.',
                },
                backUrl ? {type: 'REDIRECT', url: backUrl} : {type: 'BACK', shouldClose: true},
            ]),
        )
    }
    if (action.payload?.validationFailure?.reasonType) {
        const validationFailure = action.payload.validationFailure
        const {reasonType} = validationFailure

        if (ReloadNotAllowedReason.includes(reasonType as ReasonTypeType)) {
            return next(
                orderInterrupted([
                    {
                        type: 'ALERT',
                        message: validationFailure.fallbackMessage ?? ErrorMessage.OUT_OF_STOCK,
                    },
                    backUrl ? {type: 'REDIRECT', url: backUrl} : {type: 'BACK', shouldClose: true},
                ]),
            )
        }

        if (reasonType === OrderFailureType.ORDER_ALREADY_CREATED) {
            const orderNo = validationFailure?.reasonAttributes.orderNo
            const message = translation[language]['주문이 이미 완료되었습니다.']
            const OrderAlreadyCreatedAlert: OrderInterruptType[] = isInitActions(action)
                ? [
                      {
                          type: 'ALERT',
                          message: message ?? '이미 결제가 완료된 주문입니다. 주문완료 화면으로 이동합니다',
                      },
                  ]
                : []
            return next(
                orderInterrupted([
                    ...OrderAlreadyCreatedAlert,
                    {
                        type: 'REDIRECT',
                        url: getOrderResultUrl(state.common.entry, orderNo),
                    },
                ]),
            )
        }
    }

    return next(action)
}

const auth = authCreator<RootState>({
    validationFailureHandler,
    transactionStopHandler,
    validationFailureCodes: [...ValidationFailureCodes],
    exceptionUserAuthStatusList: [UserAuthStatus.ANONYMOUS],
})

/** 페이 회원 인증 미들웨어  */
export default auth
